import { differenceBy } from 'lodash';
import Link from 'next/link';
import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Clip } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { unselectItemAction } from '@air/redux-selected-items';
import { cn } from '@air/utils-tailwind';

import { useCreatePrivateAssetTaggingTask } from '~/components/AssetTaggingTasks/hooks/useCreatePrivateAssetTaggingTask';
import { PrivateTagsSelect } from '~/components/Tags/PrivateTagsSelect';
import { type TagBase } from '~/components/Tags/utils';
import { EditClipsTagsModalAgentPromo } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/EditClipsTagsModalAgentPromo';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { Routes } from '~/constants/routes';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useAssetVersionTagsListCommon } from '~/hooks/useAssetVersionTagsListCommon';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { useAddTagsToClips } from '~/swr-hooks/tags/useAddTagsToClips';
import { useRemoveTagsFromClips } from '~/swr-hooks/tags/useRemoveTagsFromClips';
import { insertIf } from '~/utils/insertIf';
import {
  useRemoveAssetsFromUntaggedAssetsView,
  useRemoveAssetsWithoutTagsFromFilteredViews,
  useUpdateAssetsInAllViews,
} from '~/utils/mutateUtils/GalleryAssets';

export type EditClipsTagsModalProps = {
  clipIds: Clip['id'][];
  trackLocation: AirActionTrackingLocation;
  initialTags: TagBase[];
};

export const EditClipsTagsModal = memo(
  ({ onClose, clipIds, trackLocation, initialTags }: AirModalProps<EditClipsTagsModalProps>) => {
    const dispatch = useDispatch();
    const { showToast } = useToasts();
    const { addTagsToClips } = useAddTagsToClips();
    const { removeTagsFromClips } = useRemoveTagsFromClips();
    const { updateAssetsInAllViews } = useUpdateAssetsInAllViews();
    const { removeAssetsWithoutTagsFromFilteredViews } = useRemoveAssetsWithoutTagsFromFilteredViews();
    const { removeAssetsFromUntaggedAssetsView } = useRemoveAssetsFromUntaggedAssetsView();
    const { createPrivateAssetTaggingTask } = useCreatePrivateAssetTaggingTask();
    const selectAll = useViewsSelectAllStore((state) => state.selectAll);
    const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
    const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
    const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
    const [removedClipIds, setRemovedClipIds] = useState<Clip['id'][]>([]);

    const effectiveClipIds = useMemo(
      () => clipIds.filter((id) => !removedClipIds.includes(id)),
      [clipIds, removedClipIds],
    );
    const { isFlattenedView } = useIsFlattenedView();
    const [saving, setSaving] = useState(false);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const { isWorkspaceAgentPanelProviderEnabled } = useIsWorkspaceAgentPanelAvailable();
    const showAgentTagPromo = isWorkspaceAgentPanelProviderEnabled && !selectAll && effectiveClipIds.length > 0;

    const handleRemoveClipFromSelection = useCallback(
      (clipId: Clip['id']) => {
        resetSelectAll();
        dispatch(unselectItemAction({ itemId: clipId }));
        setRemovedClipIds((prev) => (prev.includes(clipId) ? prev : [...prev, clipId]));
      },
      [dispatch, resetSelectAll],
    );

    const centralizedBoardId = useSelector(centralizedBoardIdSelector);
    const boardId = selectAll && selectAllRootBoardId ? selectAllRootBoardId : centralizedBoardId;
    const { data: fetchedCommonTags, isLoading: isLoadingCommonTags } = useAssetVersionTagsListCommon(
      { boardId, includeDescendants: includeNestedItems || isFlattenedView },
      { enabled: selectAll },
    );

    // The baseline tags used for computing diffs on save
    const baselineTags = selectAll ? (fetchedCommonTags ?? []) : initialTags;

    const [editedTags, setEditedTags] = useState<TagBase[]>(initialTags);

    useEffect(() => {
      if (fetchedCommonTags) {
        setEditedTags(fetchedCommonTags);
      }
    }, [fetchedCommonTags]);

    const handleSubmit = useCallback(async () => {
      setSaving(true);

      try {
        const tagsToAdd = differenceBy(editedTags, baselineTags, 'id');
        const tagsToRemove = differenceBy(baselineTags, editedTags, 'id');

        if (selectAll) {
          // Use background task for select-all bulk tagging
          if (!boardId) {
            showToast('Unable to determine board - please try again');
            setSaving(false);
            return;
          }

          await createPrivateAssetTaggingTask({
            boardId,
            tagIdsToAdd: tagsToAdd.map((t) => t.id),
            tagIdsToRemove: tagsToRemove.map((t) => t.id),
            includeDescendants: includeNestedItems || isFlattenedView,
            trackLocation,
          });
          onClose();
          return;
        }

        await Promise.all([
          ...insertIf(tagsToAdd.length > 0, () => [
            addTagsToClips({ tags: tagsToAdd, clipIds: effectiveClipIds, trackLocation }),
          ]),
          ...insertIf(tagsToRemove.length > 0, () => [
            removeTagsFromClips({ tags: tagsToRemove, clipIds: effectiveClipIds, trackLocation }),
          ]),
        ]);

        const tagsToRemoveIds = tagsToRemove.map(({ id }) => id);

        await updateAssetsInAllViews({
          assetIdentifiers: effectiveClipIds,
          identifier: 'id',
          boardId,
          updateFn: (clip) => {
            const existingTags = clip.tags ?? [];
            const filteredClipTags = existingTags.filter(({ id }) => !tagsToRemoveIds.includes(id));
            clip.tags = [
              ...filteredClipTags,
              ...tagsToAdd
                .filter((tag) => !existingTags.find((t) => t.id === tag.id))
                .map((tag) => ({ ...tag, isDemo: false })),
            ];
            return clip;
          },
        });

        await removeAssetsWithoutTagsFromFilteredViews({
          clipIds: effectiveClipIds,
          changedTags: [...tagsToAdd, ...tagsToRemove],
        });

        if (tagsToAdd.length > 0) {
          await removeAssetsFromUntaggedAssetsView({ clipIds: effectiveClipIds });
        }

        showToast(`Tags saved on ${pluralize('asset', effectiveClipIds.length, true)}`);
        onClose();
      } catch (_error) {
        setSaving(false);
        showToast('An error occurred - please try again later');
      }
    }, [
      editedTags,
      baselineTags,
      selectAll,
      boardId,
      showToast,
      createPrivateAssetTaggingTask,
      includeNestedItems,
      isFlattenedView,
      onClose,
      addTagsToClips,
      effectiveClipIds,
      trackLocation,
      removeTagsFromClips,
      updateAssetsInAllViews,
      removeAssetsFromUntaggedAssetsView,
      removeAssetsWithoutTagsFromFilteredViews,
    ]);

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    return (
      <Modal
        className={cn(showAgentTagPromo && 'flex flex-col gap-2 bg-transparent p-0 focus-visible:ring-0')}
        data-testid="EDIT_CLIPS_TAGS_MODAL"
        isOpen
        onDismiss={onClose}
        dangerouslyBypassFocusLock
      >
        <div
          className={cn('flex flex-col gap-6', showAgentTagPromo && 'rounded-6 ads:bg-raised bg-surface-1 p-6')}
          data-testid="EDIT_CLIPS_TAGS_MODAL_MAIN_PANEL"
        >
          <header className="flex justify-between gap-4">
            <ModalTitle>Edit tags</ModalTitle>
            <ModalCloseButton className="shrink-0" onClick={onClose} />
          </header>
          <div className="ads:text-secondary text-16 text-gray-11">
            Add or remove tags from the {pluralize('item', effectiveClipIds.length)} you’ve selected. Manage your
            workspace’s tags in{' '}
            <Link href={Routes.workspaceAdmin.tags} target="_blank">
              settings
            </Link>
            .
          </div>
          <PrivateTagsSelect selectedTags={editedTags} onSelectedTagsChanged={setEditedTags} canCreateNewTags />
          <footer className="flex justify-end gap-3">
            <Button
              data-testid="CANCEL_CLIPS_TAGS_CHANGE_BUTTON"
              appearance="ghost"
              color="gray"
              onClick={onClose}
              size="large"
            >
              Cancel
            </Button>
            <Button
              data-testid="SAVE_CLIPS_TAGS_BUTTON"
              onClick={handleSubmit}
              isLoading={saving || isLoadingCommonTags}
              disabled={
                saving ||
                isLoadingCommonTags ||
                editedTags.some(({ id }) => !id) ||
                (!selectAll && effectiveClipIds.length === 0)
              }
              appearance="filled"
              color="blue"
              size="large"
            >
              Save
            </Button>
          </footer>
        </div>
        {showAgentTagPromo ? (
          <EditClipsTagsModalAgentPromo
            clipIds={effectiveClipIds}
            onCloseModal={onClose}
            onRemoveClipFromSelection={handleRemoveClipFromSelection}
          />
        ) : null}
      </Modal>
    );
  },
);

EditClipsTagsModal.displayName = 'EditClipsTagsModal';
